import { HttpClient } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";
import { Title } from "@angular/platform-browser";
import { BehaviorSubject, Observable, catchError, map, of, tap } from "rxjs";

import { ExternalAccountOrganizationRPCClient } from "shared-grpc-api/shared/external-apis/vlp-account/vlp-account-organization-external-apis.client";
import { TenantBrandingDTO } from "shared-grpc-api/shared/domains/vlp-account/vlp-account-organization-model";
import { HostBrandingDto, TenantBranding } from "shared-domains/domains/account-management/organization/tenant-branding.model";
import { AppStorageService } from "shared-service/app-storage/app.storage";
import { VlpGrpcClients } from "shared-service/services/grpc/grpc-client.factory";

import { resolveBrandFavicon, resolveBrandIcon, resolveBrandTitle, setActiveBrand } from "./branding-assets";
import { DEFAULT_BRAND_THEME, buildBrandTheme } from "./brand-theme";

/**
 * Nguồn thương hiệu của tenant đang xem, cho toàn bộ app quản trị.
 *
 * Hai đường nạp, vì tenant được nhận biết bằng hai cách khác nhau tuỳ thời điểm:
 *
 * 1. `loadForHost()` — REST ẩn danh, chạy lúc khởi động. Màn đăng nhập phải vẽ đúng logo TRƯỚC khi
 *    biết người dùng là ai, và lúc đó hostname là manh mối duy nhất.
 * 2. `loadForTenant()` — gRPC có xác thực, chạy sau khi đăng nhập. Cần cho tenant chưa cấu hình tên
 *    miền riêng: họ làm việc trên host dùng chung nên bước 1 không suy ra được gì.
 *
 * Kết quả còn được ghi vào localStorage theo host để lần truy cập sau, đoạn script inline trong
 * index.html đổi favicon + splash NGAY, không phải chờ Angular khởi động rồi mới nháy logo một cái.
 */
@Injectable({ providedIn: "root" })
export class TenantBrandingService {
    /** Một khoá riêng cho mỗi host: cùng một trình duyệt có thể vào nhiều tenant. */
    public static storageKey(host: string): string {
        return "VLP.brand." + host;
    }

    private readonly _http = inject(HttpClient);
    private readonly _title = inject(Title);
    private readonly _storage = inject(AppStorageService);
    private readonly _grpc = inject(VlpGrpcClients);

    private readonly _branding = new BehaviorSubject<TenantBranding>(new TenantBranding());

    private _accountGrpc?: ExternalAccountOrganizationRPCClient;
    private get accountGrpc(): ExternalAccountOrganizationRPCClient {
        return (this._accountGrpc ??= new ExternalAccountOrganizationRPCClient(this._grpc.account()));
    }

    public get branding$(): Observable<TenantBranding> {
        return this._branding.asObservable();
    }

    public get current(): TenantBranding {
        return this._branding.value;
    }

    /**
     * Thương hiệu theo hostname, không cần đăng nhập. KHÔNG BAO GIỜ lỗi ra ngoài: hỏng đường này chỉ
     * mất logo riêng, không được phép chặn app khởi động.
     */
    public loadForHost(): Observable<TenantBranding> {
        return this._http.get<HostBrandingDto>("/auth/branding", { withCredentials: true }).pipe(
            map((dto) => this.fromHostDto(dto)),
            catchError(() => of(this.readCache())),
            tap((branding) => this.apply(branding)),
        );
    }

    /**
     * Đọc thương hiệu của MỘT tenant bất kỳ mà không áp lên giao diện. Dùng cho màn quản trị, nơi
     * quản trị viên nền tảng mở hồ sơ của tenant khác.
     */
    public fetch(tenantId: number): Observable<TenantBranding> {
        if (!(tenantId > 0)) {
            return of(new TenantBranding());
        }

        return new Observable<TenantBranding>((observer) => {
            this.accountGrpc.getTenantBranding({ tenantId }).response.then(
                (resp) => {
                    if (resp.status && resp.status.success === false) {
                        observer.error(resp.status);
                        return;
                    }

                    observer.next(resp.data ? this.fromDto(resp.data) : new TenantBranding());
                    observer.complete();
                },
                (error) => observer.error(error),
            );
        });
    }

    /** Thương hiệu của tenant đang đăng nhập, áp luôn lên giao diện. */
    public loadForTenant(tenantId: number): Observable<TenantBranding> {
        if (!(tenantId > 0)) {
            return of(this.current);
        }

        return this.fetch(tenantId).pipe(
            tap((branding) => this.apply(branding)),
            // Không có thương hiệu riêng thì dùng mặc định — đây không phải lỗi người dùng cần thấy.
            catchError(() => of(this.current)),
        );
    }

    /**
     * Lưu thương hiệu của một tenant. Chỉ áp dụng ngay lên giao diện khi đó đúng là tenant đang xem —
     * quản trị viên nền tảng sửa hộ tenant khác thì màn hình của chính họ phải giữ nguyên.
     */
    public save(branding: TenantBranding): Observable<TenantBranding> {
        return new Observable<TenantBranding>((observer) => {
            this.accountGrpc
                .upsertTenantBranding({
                    tenantBrandingId: branding.TenantBrandingId,
                    tenantId: branding.TenantId,
                    brandTitle: branding.BrandTitle,
                    slogan: branding.Slogan,
                    logoUrl: branding.LogoUrl,
                    iconUrl: branding.IconUrl,
                    faviconUrl: branding.FaviconUrl,
                    logoFileId: branding.LogoFileId,
                    iconFileId: branding.IconFileId,
                    faviconFileId: branding.FaviconFileId,
                    primaryColor: branding.PrimaryColor,
                    sidebarScheme: branding.SidebarScheme,
                    updatedAt: undefined,
                })
                .response.then(
                    (resp) => {
                        if (resp.status && resp.status.success === false) {
                            observer.error(resp.status);
                            return;
                        }

                        const saved = resp.data ? this.fromDto(resp.data) : branding;
                        if (saved.TenantId === this.current.TenantId) {
                            this.apply(saved);
                        }
                        observer.next(saved);
                        observer.complete();
                    },
                    (error) => observer.error(error),
                );
        });
    }

    /** Ghi thẳng bản vừa lưu từ màn quản trị, để người sửa thấy kết quả ngay không cần F5. */
    public applyDto(dto: TenantBrandingDTO): void {
        this.apply(this.fromDto(dto));
    }

    private apply(branding: TenantBranding): void {
        setActiveBrand({
            BrandTitle: branding.BrandTitle,
            Slogan: branding.Slogan,
            LogoUrl: branding.LogoUrl,
            IconUrl: branding.IconUrl,
            FaviconUrl: branding.FaviconUrl,
        });

        // Sidebar đọc logo qua storage (giá trị này có từ thời chỉ có một thương hiệu chung).
        this._storage.logoDefault = resolveBrandIcon(branding.IconUrl);
        this.applyFavicon(resolveBrandFavicon(branding.FaviconUrl));
        this.applyTitleSuffix(resolveBrandTitle(branding.BrandTitle));
        this.applyTheme(branding);
        this.writeCache(branding);
        this._branding.next(branding);
    }

    /**
     * Đổ bảng màu lên `<html>` dưới dạng biến CSS.
     *
     * Đặt ở tầng tài liệu chứ không phải trong một component, vì cùng bộ biến này phục vụ cả khung quản
     * trị lẫn màn đăng nhập — hai cây component rời nhau hoàn toàn.
     *
     * Luôn ghi ĐỦ mọi biến, kể cả khi rơi về mặc định: bấm "Về mặc định" là phải trả lại giao diện cũ
     * ngay trong phiên đang mở, mà biến CSS thì không tự biến mất — không ghi đè là màu cũ nằm lại trên
     * `<html>` cho tới lần tải lại trang.
     */
    private applyTheme(branding: TenantBranding): void {
        const root = document.documentElement;
        if (!root) {
            return;
        }

        const tokens = buildBrandTheme({
            primaryColor: branding.PrimaryColor,
            sidebarScheme: branding.SidebarScheme,
        });

        for (const name of Object.keys(DEFAULT_BRAND_THEME)) {
            root.style.setProperty(name, tokens[name] ?? DEFAULT_BRAND_THEME[name]);
        }
    }

    /**
     * Giữ nguyên phần tên trang đang hiển thị, chỉ thay hậu tố thương hiệu — hàm này chạy cả khi
     * người dùng đã ở giữa một màn nào đó (ví dụ vừa lưu logo mới).
     */
    private applyTitleSuffix(brandTitle: string): void {
        const current = this._title.getTitle();
        const pageName = current.includes("|") ? current.split("|")[0].trim() : "";
        this._title.setTitle(pageName ? pageName + " | " + brandTitle : brandTitle);
    }

    /**
     * index.html khai BA ứng viên favicon: `#favIcon` (png 256x256), một png 32x32 và một
     * `rel="shortcut icon"` .ico. Đổi mỗi `#favIcon` là không đủ — trình duyệt chọn theo CỠ nó cần,
     * và với thanh tab thì thẻ 32x32 khớp chính xác nên nó luôn thắng, tức là favicon mặc định của
     * sản phẩm vẫn hiện dù tenant đã cấu hình ảnh riêng.
     *
     * Nên gom về ĐÚNG MỘT thẻ: xoá hết ứng viên cũ rồi chèn lại một thẻ duy nhất, không khai `sizes`
     * để trình duyệt tự co ảnh. Giữ `id="favIcon"` vì đoạn script inline trong index.html tìm theo id.
     */
    private applyFavicon(href: string): void {
        const head = document.head;
        if (!head) {
            return;
        }

        // `rel~="icon"` khớp cả `rel="icon"` lẫn `rel="shortcut icon"`; `apple-touch-icon` không khớp
        // nên biểu tượng màn hình chính của iOS giữ nguyên.
        head.querySelectorAll('link[rel~="icon"]').forEach((link) => link.remove());

        const icon = document.createElement("link");
        icon.id = "favIcon";
        icon.rel = "icon";
        icon.type = href.toLowerCase().endsWith(".ico") ? "image/x-icon" : "image/png";
        icon.href = href;
        head.appendChild(icon);
    }

    private fromHostDto(dto: HostBrandingDto): TenantBranding {
        const branding = new TenantBranding();
        branding.TenantId = Number(dto?.tenantId) || 0;
        branding.BrandTitle = dto?.brandTitle ?? "";
        branding.Slogan = dto?.slogan ?? "";
        branding.LogoUrl = dto?.logoUrl ?? "";
        branding.IconUrl = dto?.iconUrl ?? "";
        branding.FaviconUrl = dto?.faviconUrl ?? "";
        branding.PrimaryColor = dto?.primaryColor ?? "";
        branding.SidebarScheme = Number(dto?.sidebarScheme) || 0;
        return branding;
    }

    private fromDto(dto: TenantBrandingDTO): TenantBranding {
        const branding = new TenantBranding();
        branding.TenantBrandingId = Number(dto.tenantBrandingId) || 0;
        branding.TenantId = Number(dto.tenantId) || 0;
        branding.BrandTitle = dto.brandTitle ?? "";
        branding.Slogan = dto.slogan ?? "";
        branding.LogoUrl = dto.logoUrl ?? "";
        branding.IconUrl = dto.iconUrl ?? "";
        branding.FaviconUrl = dto.faviconUrl ?? "";
        branding.LogoFileId = dto.logoFileId ?? "";
        branding.IconFileId = dto.iconFileId ?? "";
        branding.FaviconFileId = dto.faviconFileId ?? "";
        branding.PrimaryColor = dto.primaryColor ?? "";
        branding.SidebarScheme = Number(dto.sidebarScheme) || 0;
        return branding;
    }

    private writeCache(branding: TenantBranding): void {
        try {
            localStorage.setItem(
                TenantBrandingService.storageKey(window.location.host),
                JSON.stringify({
                    brandTitle: branding.BrandTitle,
                    iconUrl: branding.IconUrl,
                    faviconUrl: branding.FaviconUrl,
                    logoUrl: branding.LogoUrl,
                    primaryColor: branding.PrimaryColor,
                    sidebarScheme: branding.SidebarScheme,
                }),
            );
        } catch {
            /* trình duyệt chặn localStorage — chỉ mất phần vẽ sớm, không ảnh hưởng gì khác */
        }
    }

    private readCache(): TenantBranding {
        const branding = new TenantBranding();
        try {
            const raw = localStorage.getItem(TenantBrandingService.storageKey(window.location.host));
            if (raw) {
                const cached = JSON.parse(raw) as {
                    brandTitle?: string; iconUrl?: string; faviconUrl?: string;
                    logoUrl?: string; primaryColor?: string; sidebarScheme?: number;
                };
                branding.BrandTitle = cached.brandTitle ?? "";
                branding.IconUrl = cached.iconUrl ?? "";
                branding.FaviconUrl = cached.faviconUrl ?? "";
                branding.LogoUrl = cached.logoUrl ?? "";
                branding.PrimaryColor = cached.primaryColor ?? "";
                branding.SidebarScheme = Number(cached.sidebarScheme) || 0;
            }
        } catch {
            /* cache hỏng thì coi như chưa có */
        }
        return branding;
    }
}
